import React, { useEffect, useState } from 'react';
import { Modal, DatePicker, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { UPDATE_INVOICE_DUE_DATE } from '../../graphql/mutations/settlement/revenueInvoiceMutations';

interface InvoiceDueDateModalProps {
  open: boolean;
  invoiceId: string;
  /** 当前 Due Date（YYYYMMDD） */
  currentDueDate?: number | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const InvoiceDueDateModal: React.FC<InvoiceDueDateModalProps> = ({
  open,
  invoiceId,
  currentDueDate,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<Dayjs | null>(null);
  const [updateDueDate, { loading }] = useMutation(UPDATE_INVOICE_DUE_DATE);

  useEffect(() => {
    if (!open) return;
    if (currentDueDate) {
      const s = String(currentDueDate);
      // 避免依赖 dayjs CustomParseFormat 插件，手动拼成 ISO 日期
      const iso = s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s;
      const d = dayjs(iso);
      setValue(d.isValid() ? d : null);
    } else {
      setValue(null);
    }
  }, [open, currentDueDate]);

  const handleOk = async () => {
    if (!value) {
      message.warning(t('settlement.invoiceDueDateModal.selectWarning'));
      return;
    }
    const newDueDate = Number(value.format('YYYYMMDD'));
    try {
      const res = await updateDueDate({ variables: { invoiceId, newDueDate } });
      const result = res.data?.updateInvoiceDueDate;
      if (result?.success) {
        message.success(t('settlement.invoiceDueDateModal.success'));
        onSuccess();
      } else {
        message.error(result?.message || t('settlement.invoiceDueDateModal.failed'));
      }
    } catch (e) {
      message.error((e as Error)?.message || t('settlement.invoiceDueDateModal.failed'));
    }
  };

  return (
    <Modal
      open={open}
      title={t('settlement.invoiceDueDateModal.title')}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText={t('settlement.invoiceDueDateModal.okText')}
      cancelText={t('settlement.invoiceDueDateModal.cancelText')}
      destroyOnClose
    >
      <div className="text-sm text-gray-600 mb-3">
        {t('settlement.invoiceDueDateModal.description')}
      </div>
      <DatePicker
        className="w-full"
        value={value}
        onChange={setValue}
        placeholder={t('settlement.invoiceDueDateModal.placeholder') ?? ''}
        disabledDate={(d) => d && d < dayjs().startOf('day')}
      />
    </Modal>
  );
};

export default InvoiceDueDateModal;
